import { useTranslation } from "react-i18next";
import { ClockStopwatch, SlashCircle01, Tag01, Trash01, XClose } from "@untitledui/icons";

import { Button } from "@/components/base/buttons/button";
import { formatNumber } from "@/lib/format";
import { BulkTokenAction } from "@/lib/bulkTokens";

/**
 * Lišta nad tabulkou tokenů.
 *
 * Bez výběru ukazuje jen počet nalezených — stránka pak vypadá přesně jako předtím,
 * než hromadné akce existovaly (žádná prázdná lišta, žádný skok layoutu).
 *
 * **Akce se neztlumují podle dostupnosti u jednotlivých řádků.** Klient zná
 * `row.actions` jen pro viditelnou stránku; v režimu „všech N odpovídajících filtru"
 * o zbytku neví nic, takže by ztlumení bylo systematicky nesprávné. Co akce nesmí,
 * spočítá server a vrátí v `skipped`.
 */
export function BulkToolbar({
  total,
  selectedCount,
  allMatching,
  pageFullySelected,
  onSelectAllMatching,
  onClear,
  onAction,
}: {
  total: number;
  selectedCount: number;
  allMatching: boolean;
  /** Celá viditelná stránka je zaškrtnutá → má smysl nabídnout „vybrat všech N". */
  pageFullySelected: boolean;
  onSelectAllMatching: () => void;
  onClear: () => void;
  onAction: (action: BulkTokenAction) => void;
}) {
  const { t } = useTranslation();

  const label =
    selectedCount === 0
      ? t("tokens.results.count", { count: total })
      : allMatching
        ? t("tokens.bulk.allMatchingSelected", { tokens: formatNumber(selectedCount) })
        : t("tokens.bulk.selected", { tokens: formatNumber(selectedCount) });

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-tertiary">{label}</p>

      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {pageFullySelected && !allMatching && selectedCount < total && (
            <Button size="sm" color="link-color" onPress={onSelectAllMatching}>
              {t("tokens.bulk.selectAllMatching", { total: formatNumber(total) })}
            </Button>
          )}
          <Button size="sm" color="tertiary" iconLeading={XClose} onPress={onClear}>
            {t("tokens.bulk.clear")}
          </Button>
          <span aria-hidden className="mx-1 h-5 w-px bg-border-secondary" />
          <Button size="sm" color="secondary" iconLeading={ClockStopwatch} onPress={() => onAction(BulkTokenAction.EXPIRATION)}>
            {t("tokens.action.expiration")}
          </Button>
          <Button size="sm" color="secondary" iconLeading={Tag01} onPress={() => onAction(BulkTokenAction.TAGS)}>
            {t("tokens.action.tags")}
          </Button>
          <Button size="sm" color="secondary" iconLeading={SlashCircle01} onPress={() => onAction(BulkTokenAction.DEACTIVATE)}>
            {t("tokens.action.deactivate")}
          </Button>
          <Button size="sm" color="primary-destructive" iconLeading={Trash01} onPress={() => onAction(BulkTokenAction.DELETE)}>
            {t("tokens.action.delete")}
          </Button>
        </div>
      )}
    </div>
  );
}
